/**
 * Presentation formatting — the read-aloud numeral rule and the clinic
 * calendar system, in one place. Wire values stay ISO instants / plain
 * numbers; Jalali vs Gregorian and Persian digits are display-only,
 * matching the backend's calendar-independent storage.
 */

import type { CalendarSystem } from '../clinic/api';
import type { Locale } from '../i18n/LocaleProvider';
import { isoToParts } from './dateValue';

/** Read-aloud numbers (counts, ages, money digits): locale digits. */
export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === 'fa' ? 'fa-IR' : 'en-US', {
    useGrouping: false,
  }).format(value);
}

/** Convert ASCII digits inside a string to locale digits (read-aloud islands). */
export function localizeDigits(text: string, locale: Locale): string {
  if (locale !== 'fa') return text;
  const fa = '۰۱۲۳۴۵۶۷۸۹';
  return text.replace(/[0-9]/g, (d) => fa[Number(d)] ?? d);
}

/**
 * Presentation only: an unparseable instant renders as an em-dash instead of
 * letting Intl throw and take the page down (the crash path of a cleared
 * date input). Date MATH stays strict — this leniency is display-side only.
 */
function asDate(iso: string | Date): Date | null {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return Number.isNaN(d.getTime()) ? null : d;
}

function intlLocale(locale: Locale, calendar: CalendarSystem): string {
  const base = locale === 'fa' ? 'fa-IR' : 'en-US';
  const cal = calendar === 'jalali' ? 'persian' : 'gregory';
  return `${base}-u-ca-${cal}`;
}

export function formatDate(
  iso: string | Date,
  opts: { locale: Locale; calendar: CalendarSystem; timezone: string },
): string {
  const d = asDate(iso);
  if (d === null) return '—';
  return new Intl.DateTimeFormat(intlLocale(opts.locale, opts.calendar), {
    dateStyle: 'medium',
    timeZone: opts.timezone,
  }).format(d);
}

export function formatWeekdayDate(
  iso: string | Date,
  opts: { locale: Locale; calendar: CalendarSystem; timezone: string },
): string {
  const d = asDate(iso);
  if (d === null) return '—';
  return new Intl.DateTimeFormat(intlLocale(opts.locale, opts.calendar), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: opts.timezone,
  }).format(d);
}

/** The weekday alone, in the clinic timezone — "Tuesday" / «سه‌شنبه». */
export function formatWeekday(
  iso: string | Date,
  opts: { locale: Locale; calendar: CalendarSystem; timezone: string },
): string {
  const d = asDate(iso);
  if (d === null) return '—';
  return new Intl.DateTimeFormat(intlLocale(opts.locale, opts.calendar), {
    weekday: 'long',
    timeZone: opts.timezone,
  }).format(d);
}

/** 24-hour clock always (frozen rule). */
export function formatTime(iso: string | Date, opts: { locale: Locale; timezone: string }): string {
  const d = asDate(iso);
  if (d === null) return '—';
  return new Intl.DateTimeFormat(opts.locale === 'fa' ? 'fa-IR' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: opts.timezone,
  }).format(d);
}

/**
 * A DATE-ONLY wire value ('YYYY-MM-DD') shown in the clinic calendar with no
 * timezone shift — an intake medication anchor, an instrument administration
 * date. Instants go through formatDate; a calendar date must never be
 * re-interpreted through a timezone.
 */
export function formatDateOnly(iso: string, cal: CalendarSystem, locale: Locale): string {
  const p = isoToParts(iso, cal);
  if (!p) return iso;
  const mm = String(p.month).padStart(2, '0');
  const dd = String(p.day).padStart(2, '0');
  return localizeDigits(`${p.year}/${mm}/${dd}`, locale);
}
